import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { CartStore } from './cart.types';
import { syncFromDB } from './cart-db';
import {
  addItem as addItemAction,
  forceAddItem as forceAddItemAction,
  updateQuantity as updateQuantityAction,
  removeItem as removeItemAction,
  clearCart as clearCartAction
} from './cart-actions';
import {
  getTotalPrice,
  getFinalPrice,
  getItemCount,
  canAddMoreItems,
  validateStock
} from './cart-computed';

// Re-export types for backwards compatibility
export type { CartItem, CartStore, OrderType, PickupMethod } from './cart.types';


export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      // Initial state
      storeId: null,
      storeName: null,
      orderType: null,
      items: [],
      pickupTime: null,
      desiredPickupTime: null,
      pickupDate: null,
      pickupMethod: 'PICKUP',
      usedPoints: 0,
      
      // Actions with separated logic
      addItem: async (storeId, storeName, orderType, item, userId) => {
        const state = get();
        return await addItemAction(
          storeId, storeName, orderType, item, userId,
          state, 
          (updates) => set({ ...state, ...updates }), 
          get
        );
      },

      forceAddItem: async (storeId, storeName, orderType, item, userId) => {
        const state = get();
        await forceAddItemAction(
          storeId, storeName, orderType, item, userId,
          state,
          (updates) => set({ ...state, ...updates }),
          () => get().clearCart(userId),
          () => get().addItem(storeId, storeName, orderType, item, userId)
        );
      },
      
      // Update item quantity with DB synchronization
      updateQuantity: async (productId, quantity, userId) => {
        const state = get();
        await updateQuantityAction(
          productId, quantity, userId,
          state,
          (updates) => set({ ...state, ...updates })
        );
      },
      
      // Remove item from cart with DB synchronization
      removeItem: async (productId, userId) => {
        const state = get();
        await removeItemAction(
          productId, userId,
          state,
          (updates) => set({ ...state, ...updates })
        );
      },
      
      // Clear entire cart with DB synchronization
      clearCart: async (userId) => {
        await clearCartAction(
          userId,
          (updates) => set(updates)
        );
      },
      
      // Sync cart from DB to local state
      syncFromDB: async (userId) => {
        await syncFromDB(userId, set);
      },
      
      // Set used points
      setUsedPoints: (points) => {
        const maxPoints = get().getTotalPrice();
        set({ usedPoints: Math.min(Math.max(0, points), maxPoints) });
      },
      
      // Set pickup time
      setPickupTime: (time) => {
        set({ pickupTime: time });
      },
      
      // Set pickup date (for reservation)
      setPickupDate: (date) => {
        set({ pickupDate: date });
      },
      
      // Set desired pickup time (for reservation discount)
      setDesiredPickupTime: (time) => {
        set({ desiredPickupTime: time });
      },
      
      // Set pickup method
      setPickupMethod: (method) => {
        set({ pickupMethod: method });
      },
      
      // Validate stock for all items in cart
      validateStock: async () => {
        const { items } = get();
        return await validateStock(items);
      },
      
      // Calculate total price
      getTotalPrice: () => {
        return getTotalPrice(get().items);
      },
      
      // Calculate final price after points
      getFinalPrice: (availablePoints = 0) => {
        const { items, usedPoints } = get();
        return getFinalPrice(items, usedPoints, availablePoints);
      },
      
      // Get total item count
      getItemCount: () => {
        return getItemCount(get().items);
      },
      
      // Check if can add more items
      canAddMoreItems: () => {
        return canAddMoreItems(get().items);
      },
    }),
    {
      name: 'cart-storage',
      storage: createJSONStorage(() => localStorage),
      // 하이드레이션 시 잘못 저장된 재고값(999/null 등) 정제
      merge: (persistedState, currentState) => {
        const merged = { ...currentState, ...(persistedState as any) } as any;

        if (merged.items && Array.isArray(merged.items)) {
          merged.items = merged.items.map((item: any) => {
            const stock = item?.currentStock;
            let normalizedStock = stock;

            if (stock === 999 || stock === null || stock === undefined) {
              normalizedStock = 5; // 안전값
            } else if (typeof stock === 'number' && stock < 0) {
              normalizedStock = 0;
            }

            return { ...item, currentStock: normalizedStock };
          });
        }

        return merged;
      },
      // DB를 진실의 원천으로 설정 - 로그인한 사용자는 DB 데이터를 우선
      partialize: (state) => ({
        // localStorage에는 최소한의 정보만 저장
        // 실제 데이터는 DB에서 불러옴
        storeId: state.storeId,
        storeName: state.storeName,
        orderType: state.orderType,
        itemCount: state.items.length,
      }),
    }
  )
);
